import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Loader2, Filter } from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from "recharts";
import { SourceBreakdownTable } from "@/components/enquiries/analytics/SourceBreakdownTable";

const SOURCES = [
  { key: "website", label: "Website" }, { key: "facebook", label: "Facebook" },
  { key: "instagram", label: "Instagram" }, { key: "phone", label: "Phone" },
  { key: "email", label: "Email" }, { key: "walkin", label: "Walk-in" },
  { key: "sms", label: "SMS" }, { key: "referral", label: "Referral" }, { key: "other", label: "Other" },
];

const pct = (x) => (x == null ? "—" : `${Math.round(x * 100)}%`);

function PeriodToggle({ value, onChange }) {
  return (
    <div className="inline-flex rounded-md border border-zinc-300 overflow-hidden" data-testid="conversion-period-toggle">
      {[{ key: "week", label: "Weekly" }, { key: "month", label: "Monthly" }].map((p, i) => (
        <button
          key={p.key}
          onClick={() => onChange(p.key)}
          className={`px-3 h-7 text-xs transition-colors ${value === p.key ? "bg-zinc-950 text-white" : "bg-white text-zinc-700 hover:bg-zinc-50"} ${i ? "border-l border-zinc-300" : ""}`}
          data-testid={`conversion-period-${p.key}`}
        >{p.label}</button>
      ))}
    </div>
  );
}

function FunnelTable({ rows, totals }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs" data-testid="conversion-table">
        <thead className="text-2xs uppercase tracking-wider text-zinc-500 border-b border-zinc-200">
          <tr>
            <th className="text-left py-1.5">First enquired</th>
            <th className="text-right py-1.5">New leads</th>
            <th className="text-right py-1.5">Reached prospect</th>
            <th className="text-right py-1.5">Became customer</th>
            <th className="text-right py-1.5">Lost</th>
            <th className="text-right py-1.5">Still lead</th>
            <th className="text-right py-1.5" title="Already clients when they first enquired — not counted in conversion">Existing clients</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-zinc-100 font-mono-num">
          {[...rows].reverse().map((r) => (
            <tr key={r.period} data-testid={`conversion-row-${r.period}`}>
              <td className="py-1.5 text-zinc-900 font-sans">{r.label}</td>
              <td className="text-right text-zinc-900">{r.new_leads}</td>
              <td className="text-right text-amber-800">{r.reached_prospect} <span className="text-zinc-400">{pct(r.prospect_rate)}</span></td>
              <td className="text-right text-emerald-700">{r.became_customer} <span className="text-zinc-400">{pct(r.customer_rate)}</span></td>
              <td className="text-right text-zinc-600">{r.lost}</td>
              <td className="text-right text-sky-800">{r.still_lead}</td>
              <td className="text-right text-zinc-500">{r.existing_clients}</td>
            </tr>
          ))}
        </tbody>
        <tfoot className="border-t-2 border-zinc-300 font-mono-num font-semibold">
          <tr data-testid="conversion-totals">
            <td className="py-1.5 font-sans">Total</td>
            <td className="text-right">{totals.new_leads}</td>
            <td className="text-right text-amber-800">{totals.reached_prospect} <span className="text-zinc-400">{pct(totals.prospect_rate)}</span></td>
            <td className="text-right text-emerald-700">{totals.became_customer} <span className="text-zinc-400">{pct(totals.customer_rate)}</span></td>
            <td className="text-right text-zinc-600">{totals.lost}</td>
            <td className="text-right text-sky-800">{totals.still_lead}</td>
            <td className="text-right text-zinc-500">{totals.existing_clients}</td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

export function ConversionFunnelCard() {
  const [period, setPeriod] = useState("month");
  const [source, setSource] = useState("");
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setData(null);
    const params = { period, periods: 12, ...(source ? { source } : {}) };
    api.get("/enquiries/analytics/conversion", { params })
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => { if (!cancelled) setData({ rows: [], totals: {}, by_source: [] }); });
    return () => { cancelled = true; };
  }, [period, source]);

  const chartRows = (data?.rows || []).map((r) => ({
    ...r, short: period === "week" ? r.period.slice(5).split("-").reverse().join("/") : r.label,
  }));

  return (
    <Card className="p-4 rounded-md shadow-none border border-zinc-200 space-y-3" data-testid="analytics-conversion-funnel">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <div className="text-2xs uppercase tracking-wider text-zinc-500 flex items-center gap-1.5">
            <Filter className="h-3.5 w-3.5" /> Lead → Prospect → Customer conversion
          </div>
          <div className="text-2xs text-zinc-400 mt-0.5">
            People grouped by the {period === "week" ? "week" : "month"} they first enquired · last 12 {period === "week" ? "weeks" : "months"}
            {source && <> · <b className="text-zinc-600" data-testid="conversion-source-active">{SOURCES.find((s) => s.key === source)?.label} only</b></>}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={source}
            onChange={(e) => setSource(e.target.value)}
            className="h-7 border border-zinc-300 rounded-md px-2 text-xs bg-white"
            data-testid="conversion-source-select"
          >
            <option value="">All sources</option>
            {SOURCES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
          </select>
          <PeriodToggle value={period} onChange={setPeriod} />
        </div>
      </div>
      {!data ? (
        <div className="text-xs text-zinc-500 flex items-center gap-2"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading…</div>
      ) : (
        <>
          <div className="h-[220px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartRows}>
                <CartesianGrid stroke="#f1f5f9" vertical={false} />
                <XAxis dataKey="short" tick={{ fontSize: 10 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 10 }} width={30} />
                <Tooltip contentStyle={{ fontSize: 12 }} labelFormatter={(_, p) => p?.[0]?.payload?.label} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="new_leads" name="New leads" fill="#0284c7" radius={[3, 3, 0, 0]} />
                <Bar dataKey="reached_prospect" name="Reached prospect" fill="#d97706" radius={[3, 3, 0, 0]} />
                <Bar dataKey="became_customer" name="Became customer" fill="#059669" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <FunnelTable rows={data.rows || []} totals={data.totals || {}} />
          <SourceBreakdownTable rows={data.by_source || []} period={period} />
        </>
      )}
    </Card>
  );
}
